import React from 'react'
import { Volume2, VolumeX, Copy, Download, Share2Icon } from 'lucide-react';
import { ProgressSlider } from "@/components/common/progress-slider";
import { IconButton } from '@/components/common/icon-button';
import { Seek0Icon } from '@/components/agent-creation/assets/seek0';
import { Seek1Icon } from '@/components/agent-creation/assets/seek1';
import { AudioPlayState } from '@/manager/manager/audio-controller-manager';
import { useToast } from "@/hooks/use-toast";
import { useDownload } from "@/hooks/use-download";
import VolumeControl from '../modal-content-detail/volume-control';
import { AudioVolumeControl } from '../audio-volume-control';
import { AgentShareModal } from '../agent-share-modal';
import { ShareIcon } from '../agent-share-modal/assets';
import { useTranslation } from 'react-i18next';

interface AudioControllerBarProps {
  audioUrl: string;
  copyContent?: string;
  /** 是否显示分享按钮 */
  showShare?: boolean;

  playState: AudioPlayState;
  duration: number;
  currentTime: number;
  playbackRate: number;
  muted: boolean;
  volume: number;
  setVolume: (volume: number) => void;
  setMuted: (muted: boolean) => void;
  seekTo: (time: number) => void;
  setPlaybackRate: (rate: number) => void;
  startPlaybackRateDrag: () => void;
  endPlaybackRateDrag: () => void;
  toggleMuted: () => void;
}

// 音频控制器栏
export const AudioControllerBar: React.FC<AudioControllerBarProps> = ({
  audioUrl,
  copyContent,
  showShare,

  playState,
  duration,
  currentTime,
  playbackRate,
  muted,
  volume,
  seekTo,
  setVolume,
  setMuted,
  setPlaybackRate,
  startPlaybackRateDrag,
  endPlaybackRateDrag,
  toggleMuted,
}) => {
  const { t } = useTranslation();

  const handleSpeedToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const steps = [1, 1.25, 1.5, 2];
    const idx = steps.findIndex((r) => Math.abs(r - playbackRate) < 0.001);
    const next = steps[(idx + 1) % steps.length];
    setPlaybackRate(next);
  };

  const handleVolumeToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleMuted();
  };

  const handleSeekBack15 = (e: React.MouseEvent) => {
    e.stopPropagation();
    seekTo(Math.max(0, currentTime - 15));
  };

  const handleSeekForward15 = (e: React.MouseEvent) => {
    e.stopPropagation();
    seekTo(Math.min(duration || 0, currentTime + 15));
  };

  const { download } = useDownload();
  const { toast } = useToast();
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyContent || '');
      toast.default({ description: t('agent_creation.agent_modal.copied') });
    } catch {
      toast.default({ description: t('agent_creation.agent_modal.copy_fail') });
    }
  };
  const handleDownload = async () => {
    try {
      await download(audioUrl, copyContent || 'audio');
      toast.default({ description: t('agent_creation.agent_modal.download_complete') });
    } catch {
      toast.default({ description: t('agent_creation.agent_modal.download_fail') });
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return time ? `${minutes}:${seconds.toString().padStart(2, "0")}` : "0:00";
  };

  const speedLabel = t('agent_creation.agent_modal.playback_rate_label', { rate: playbackRate });
  const currentTimeLabel = formatTime(currentTime);
  const durationLabel = formatTime(duration);

  return (
    <div className="mt-4">
      <div className="flex items-center gap-4">
        {/* 倍速/音量/快退快进 */}
        <button type="button" className="w-8 h-8 text-[rgba(29,41,57,1)] text-xs font-medium" onClick={handleSpeedToggle}>
          {speedLabel}
        </button>
        <AudioVolumeControl
          initialVolume={volume * 100}
          initialMuted={muted}
          onVolumeChange={(newVolume) => {
            // 调用音频控制器的方法来设置音量
            setVolume(newVolume);
          }}
          onMutedChange={(newMuted) => {
            // 调用音频控制器的方法来设置静音状态
            setMuted(newMuted);
          }}
        />
        <IconButton
          icon={<Seek0Icon className="w-5 h-5 text-[rgba(29,41,57,1)]" />}
          onClick={handleSeekBack15}
          aria-label="后退15秒"
        />
        <IconButton
          icon={<Seek1Icon className="w-5 h-5 text-[rgba(29,41,57,1)]" />}
          onClick={handleSeekForward15}
          aria-label="前进15秒"
        />

        {/* 当前播放进度（mm:ss） */}
        <span className="text-xs text-[rgba(28,36,51,1)]">{currentTimeLabel}</span>

        {/* 进度条（支持拖拽） */}
        <ProgressSlider
          value={currentTime}
          max={duration || 0}
          trackColor="rgb(234,236,240)"
          progressColor="rgb(28,36,51)"
          thumbColor="rgb(255,255,255)"
          thumbBorderColor="rgb(28,36,51)"
          onChange={seekTo}
          onDragStart={startPlaybackRateDrag}
          onDragEnd={endPlaybackRateDrag}
          className="flex-1 "
        />

        {/* 总时长（mm:ss） */}
        <span className="text-xs text-[rgba(28,36,51,1)]">{durationLabel}</span>

        {/* 复制 / 下载 */}
        <IconButton
          icon={<Copy className="w-5 h-5" />}
          onClick={(e) => { e.stopPropagation(); handleCopy(); }}
          aria-label="复制文案"
        />
        <IconButton
          icon={<Download className="w-5 h-5" />}
          onClick={(e) => { e.stopPropagation(); handleDownload(); }}
          aria-label="下载音频"
        />
        {showShare && (
          <AgentShareModal
            showXiaoyuzhou
            showXiMalaya
            trigger={
              <IconButton
                icon={<ShareIcon className="w-5 h-5" />}
              />
            }
          />
        )}
      </div>
    </div>
  )
}
